// The hosted checkout's return pages: /checkout/success, /checkout/cancelled, /checkout/pending (`?order=<order_id>`).
// Rules in src/checkout.ts: the URL is only a hint, status comes from money with the session, nothing is bought here.
// When money isn't selling (src/selling.ts) they redirect to the home page.

import { api } from "../api.ts";
import { parseReturn, pickOrder, POLL_DELAYS_MS, returnLink, returnView, type OrderStatus, type ReturnHint } from "../checkout.ts";
import { clear, h, link } from "../dom.ts";
import { launch } from "../play.ts";
import { navigate, setTitle } from "../router.ts";
import { signedIn } from "../session.ts";
import { checkoutRedirect, selling } from "../selling.ts";

/** This order as money sees it (only the buyer's own orders are listed); null when unknown. */
async function orderStatus(orderId: string): Promise<OrderStatus | null> {
  return api("money", "/me/orders").then((r) => pickOrder(r, orderId), () => null);
}

export async function checkoutReturnPage(params: Record<string, string>, query: URLSearchParams): Promise<HTMLElement> {
  // no selling (wave 9): there is no checkout to come back from, so these pages go home
  const away = checkoutRedirect(selling());
  if (away) {
    navigate(away, true);
    return h("div");
  }
  // a membership checkout (docs/backend/money.md "Memberships") comes back here too: its page shows the status
  if (query.has("membership")) {
    navigate(query.has("cancelled") ? "/membership" : "/membership?joined=1", true);
    return h("div");
  }
  const { hint, orderId } = parseReturn(params.state, query);
  // keep only the order id: whatever else the checkout appended (its own ids, amounts) leaves the address bar
  history.replaceState(null, "", location.pathname + (orderId ? `?order=${orderId}` : ""));
  const order = orderId && signedIn() ? await orderStatus(orderId) : null;
  const box = h("div", { "aria-live": "polite" });
  const back = returnLink(orderId);
  const page = h(
    "div",
    { class: "empty checkout-return" },
    box,
    h(
      "p",
      { class: "actions" },
      back
        ? h(
            "a",
            {
              class: "btn",
              href: back,
              onclick: (e: Event) => {
                e.preventDefault();
                launch("", { link: back, onMissing: () => navigate("/download") });
              },
            },
            "Back to Banger",
          )
        : null,
      link("/browse", { class: back ? "btn ghost" : "btn" }, "Browse games"),
    ),
    orderId && !signedIn() ? h("p", { class: "dim small" }, link(`/signin?next=${encodeURIComponent(`/checkout/${hint === "unknown" ? "success" : hint}?order=${orderId}`)}`, null, "Sign in"), " to see this top-up here. Your balance is always in Banger.") : null,
    h("p", { class: "dim small" }, "Top-ups start in Banger and are spent there; this site never charges you."),
  );
  draw(box, hint, order);
  if (orderId && order && returnView(hint, order).poll) void poll(box, hint, orderId, page);
  return page;
}

function draw(box: HTMLElement, hint: ReturnHint, order: OrderStatus | null): void {
  const v = returnView(hint, order);
  setTitle(v.heading);
  clear(box, h("h1", null, v.heading), h("p", { class: "lead" }, v.body), v.poll && order ? h("p", { class: "dim small" }, "Checking…") : null);
}

/** A pending order: ask money a few more times (about 30 s), then leave the "on the way" message up. */
async function poll(box: HTMLElement, hint: ReturnHint, orderId: string, page: HTMLElement): Promise<void> {
  let last: OrderStatus | null = null;
  for (const ms of POLL_DELAYS_MS) {
    await new Promise((r) => setTimeout(r, ms));
    if (!page.isConnected) return; // navigated away
    const o = await orderStatus(orderId);
    if (o) last = o;
    if (o && o.status !== "pending" && o.status !== "expired") return draw(box, hint, o);
  }
  const v = returnView(hint, last);
  clear(box, h("h1", null, v.heading), h("p", { class: "lead" }, v.body), last?.status === "pending" ? h("p", { class: "dim small" }, "Still being confirmed. Banger shows the top-up once it's done.") : null);
}
